/**
 * Capital concreto en el upsell native (30-oct-2026). Hasta ahora el cliente solo
 * podía elegir entre base ±25.000 y, para llegar a un capital concreto, tenía que
 * elegir y recargar varias veces. Lógica pura (sin React) para poder probarla:
 * leer lo que escribe el cliente en formato italiano, validarlo contra los límites
 * y ajustarlo al paso de la rejilla de AGL.
 */

export interface CapitalLimits {
  minCapital: number;
  maxCapital: number;
  step: number;
}

/**
 * Rejilla de AGL (`agl_base_prices` v2): 25.000–350.000 de 5.000 en 5.000, igual
 * para todas las edades. El gateway la devuelve en `limits`; esto es la red si
 * aún no la manda.
 */
export const DEFAULT_CAPITAL_LIMITS: CapitalLimits = {
  minCapital: 25000,
  maxCapital: 350000,
  step: 5000,
};

const isPositiveInt = (value: unknown): value is number =>
  "number" === typeof value && Number.isInteger(value) && value > 0;

/** Límites de la respuesta del upsell, o los de la rejilla si no vienen o no cuadran. */
export function readCapitalLimits(raw: unknown): CapitalLimits {
  if (null === raw || "object" !== typeof raw) return DEFAULT_CAPITAL_LIMITS;
  const { minCapital, maxCapital, step } = raw as Record<string, unknown>;
  if (!isPositiveInt(minCapital) || !isPositiveInt(maxCapital) || !isPositiveInt(step) || minCapital >= maxCapital) {
    return DEFAULT_CAPITAL_LIMITS;
  }
  return { minCapital, maxCapital, step };
}

/**
 * Importe escrito por el cliente → euros enteros. Acepta el formato italiano
 * («125.000», «125 000», «125.000 €», «125.000,00»); los decimales se ignoran.
 * null si no es un importe.
 */
export function parseCapitalInput(text: string): number | null {
  const compact = text.replace(/[\s.'’€]/g, "");
  if (!/^\d+(,\d*)?$/.test(compact)) return null;
  const euros = parseInt(compact.split(",")[0], 10);
  return Number.isFinite(euros) ? euros : null;
}

export type CapitalCheck =
  | { kind: "ok"; capital: number; adjusted: boolean }
  | { kind: "invalid" }
  | { kind: "belowMin" }
  | { kind: "aboveMax" };

/** Valida contra los límites y ajusta al paso más cercano (avisando si cambia). */
export function checkCapital(value: number | null, limits: CapitalLimits): CapitalCheck {
  if (null === value) return { kind: "invalid" };
  if (value < limits.minCapital) return { kind: "belowMin" };
  if (value > limits.maxCapital) return { kind: "aboveMax" };
  const snapped = Math.min(
    limits.maxCapital,
    Math.max(limits.minCapital, Math.round(value / limits.step) * limits.step),
  );
  return { kind: "ok", capital: snapped, adjusted: snapped !== value };
}

/** Un paso arriba o abajo desde el valor actual, dentro de los límites. */
export function stepCapital(current: number | null, direction: 1 | -1, limits: CapitalLimits): number {
  const base = null === current ? limits.minCapital : current;
  // Fuera de paso, el primer clic lleva al paso siguiente (o anterior) de la rejilla.
  const next =
    direction > 0
      ? Math.floor(base / limits.step) * limits.step + limits.step
      : Math.ceil(base / limits.step) * limits.step - limits.step;
  return Math.min(limits.maxCapital, Math.max(limits.minCapital, next));
}

/**
 * «125.000» (sin símbolo), para el valor del input. Separador de miles a mano:
 * `toLocaleString("it-IT")` no agrupa los de 4 cifras («5000») y el paso se leería
 * distinto que los límites («25.000»).
 */
export const formatCapitalNumber = (value: number): string =>
  Math.round(value)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ".");

/** «125.000 €», para los textos. */
export const formatCapitalEuro = (value: number): string => `${formatCapitalNumber(value)} €`;
